import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { generateDxfEquipmentOverlays } from '../features/equipment/generateDxfOverlaysThunk';

/**
 * Re-generates the DXF equipment overlays whenever DXF layer or entity-type
 * visibility changes.
 *
 * Overlays are destroyed and rebuilt rather than merely hidden, so the store
 * only ever holds overlays the user can see — which is what keeps them out of
 * saved layouts, backend saves and simulation payloads. Does nothing until the
 * user has generated overlays at least once (View → Generate Overlays).
 */
export function useDxfOverlayRegeneration(): void {
  const dispatch = useAppDispatch();
  const generated = useAppSelector((state) => state.equipment.dxfOverlaysGenerated);
  const layers = useAppSelector((state) => state.floorplan.layers);
  const hiddenEntityTypes = useAppSelector((state) => state.floorplan.hiddenEntityTypes);

  const visibilityKey = `${layers.map((l) => `${l.name}:${l.visible ? 1 : 0}`).join('|')}#${Object.keys(
    hiddenEntityTypes
  )
    .filter((key) => hiddenEntityTypes[key])
    .sort()
    .join(',')}`;

  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!generated) {
      lastKeyRef.current = null;
      return;
    }
    if (lastKeyRef.current === visibilityKey) return;
    const firstRunAfterGenerate = lastKeyRef.current === null;
    lastKeyRef.current = visibilityKey;
    // The generate action already built overlays for the current visibility, so
    // only an actual change needs a rebuild.
    if (firstRunAfterGenerate) return;
    dispatch(generateDxfEquipmentOverlays());
  }, [generated, visibilityKey, dispatch]);
}
